import { Badge, Button, Skeleton } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { IconBell, IconChevronLeft } from '@tabler/icons-react';
import clsx from 'clsx';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useEffect, useMemo, useState } from 'react';
import { aiChatLogsAtomFamily } from '~/atoms/ai-chat-atoms';
import {
  MIN_AI_CHAT_PANEL_WIDTH,
  MIN_NO_CODE_EDITOR_PANEL_WIDTH,
  noCodeEditorPanelWidthAtom,
  rightPanelWidthAtom,
  testStartingUrlAtom,
  consolePanelCollapsedAtom,
  consolePanelHeightAtom,
} from '~/atoms/atoms';
import { activeStepAtom, selectedStepsIdsAtom } from '~/atoms/no-code-atoms';
import { recorderStateAtom } from '~/atoms/recorder-atoms';
import { HeadTitle } from '~/components/common/HeadTitle';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import { api } from '~/trpc-extra/api';
import { AIChatPanel } from '../browser/status-panel/panels/chat/AIChatPanel';
import { AIChatPanelSkeleton } from '../browser/status-panel/panels/chat/AIChatPanelSkeleton';
import BrowserPanel from '../test/BrowserPanel';
import EditorPanel from '../test/EditorPanel/EditorPanel';
import EditorPanelSkeleton from '../test/EditorPanel/EditorPanelSkeleton';
import TestSplitPane from '../test/TestSplitPane';
import { ConsolePanel } from './ConsolePanel';
import TestDraftHeader from './header/TestDraftHeader';
import TestDraftHeaderSkeleton from './TestDraftHeaderSkeleton';

export default function TestDraftView({ testDraftId, fromTest }: { testDraftId: string; fromTest?: boolean }) {
  const { data: testContent, isLoading } = api.testContent.get.useQuery(
    { id: testDraftId },
    { enabled: !!testDraftId, refetchOnWindowFocus: false },
  );

  const { vmStatus } = useLiveStateManager();
  const resetActiveStepId = useResetAtom(activeStepAtom);
  const setTestStartingUrl = useSetAtom(testStartingUrlAtom);
  const resetSelectedStepsIds = useResetAtom(selectedStepsIdsAtom);
  const resetTestStartingUrl = useResetAtom(testStartingUrlAtom);
  const recorderState = useAtomValue(recorderStateAtom);
  const [noCodeEditorPanelWidth, setNoCodeEditorPanelWidth] = useAtom(noCodeEditorPanelWidthAtom);
  const [rightPanelWidth, setRightPanelWidth] = useAtom(rightPanelWidthAtom);
  const { ref: innerSplitPaneWrapperRef, width: innerWidth, height: innerHeight } = useElementSize();

  const [consoleHeight, setConsoleHeight] = useAtom(consolePanelHeightAtom);
  const [isConsoleCollapsed, setIsConsoleCollapsed] = useAtom(consolePanelCollapsedAtom);
  const [isAIChatPanelVisible, setIsAIChatPanelVisible] = useState(true);
  const [lastSeenMessageCount, setLastSeenMessageCount] = useState(0);

  // Get AI chat logs to show notification when panel is hidden
  const aiChatLogsAtom = aiChatLogsAtomFamily(testContent?.id || '');
  const [aiChatLogs, setAILogs] = useAtom(aiChatLogsAtom);
  const activeAiChatLog = aiChatLogs.logs.at(-1) ?? [];
  const hasNewMessages = activeAiChatLog.length > lastSeenMessageCount;

  // Update last seen message count when panel becomes visible
  useEffect(() => {
    if (isAIChatPanelVisible) {
      setLastSeenMessageCount(activeAiChatLog.length);
    }
  }, [isAIChatPanelVisible, activeAiChatLog.length]);

  const hasTestCode = useMemo(() => {
    return (testContent?.steps || []).length > 0 || testContent?.test?.testType === 'MOBILE_APP';
  }, [testContent]);

  useEffect(() => {
    return () => {
      resetSelectedStepsIds();
      resetTestStartingUrl();
    };
  }, [resetSelectedStepsIds, resetTestStartingUrl]);

  useEffect(() => {
    return () => {
      resetActiveStepId();
    };
  }, [resetActiveStepId]);

  useEffect(() => {
    if (testContent?.steps.length !== undefined && testContent?.steps[0]?.operations.length !== undefined) {
      setTestStartingUrl(testContent?.steps[0]?.operations[0]?.navigationOp?.value);
    }
  }, [setTestStartingUrl, testContent]);

  const toggleAIChatPanel = () => {
    // When opening the AI chat panel after it was collapsed and there are new unseen messages,
    // automatically switch to the latest log so users see the newest content immediately.
    if (!isAIChatPanelVisible && hasNewMessages && testContent?.id) {
      setAILogs({ type: 'navigateLast' });
    }

    setIsAIChatPanelVisible(!isAIChatPanelVisible);
  };

  // Common resizer class for vertical split pane
  const resizerClassName = clsx(
    'transition-all z-10 border-[var(--mantine-color-body)]',
    'w-[4px] border-x cursor-col-resize',
    'bg-gray-300 dark:bg-gray-700',
    'hover:bg-gradient-to-r hover:from-gray-300 hover:to-gray-200 dark:hover:from-gray-500 dark:hover:to-gray-400',
  );

  // Cleaning up any preview temporary step data

  return (
    <>
      <HeadTitle
        title={testContent?.test?.name}
        suffix="Draft"
      />
      <div className="flex h-full flex-col">
        {!testContent || isLoading ?
          <TestDraftHeaderSkeleton />
        : <TestDraftHeader
            testDraftId={testDraftId}
            testId={testContent.test.id}
            testName={testContent.test.name}
            gitBranch={testContent.gitBranch || undefined}
            hasTestCode={hasTestCode}
            fromTest={fromTest}
          />
        }
        <div className="relative h-full w-full">
          {/* Outer Split: Editor vs (Browser + Status) */}
          <TestSplitPane
            split="vertical"
            defaultSize={noCodeEditorPanelWidth}
            minSize={MIN_NO_CODE_EDITOR_PANEL_WIDTH} // Keep original minSize for editor
            maxSize={typeof window !== 'undefined' ? window.innerWidth * 0.7 : 1000} // Keep original maxSize
            onChange={setNoCodeEditorPanelWidth}
            onDragFinished={setNoCodeEditorPanelWidth}
            // Prevent the second pane (right side) from triggering resize on the outer splitter
            pane2Style={{ pointerEvents: 'none' }} // Disable pointer events on the right pane container
            resizerStyle={{ pointerEvents: 'auto' }} // Re-enable pointer events specifically for the resizer
            resizerClassName={resizerClassName}
          >
            {/* Left Pane: Editor */}
            {isLoading ?
              <EditorPanelSkeleton />
            : testContent && (
                <EditorPanel
                  testContentId={testContent.id}
                  // TODO: this is not correct, we should check if the test is readonly
                  // This should be passed as a separate prop called 'disabled' or something
                  readonly={
                    vmStatus?.state === 'loading' ||
                    vmStatus?.state === 'replaying' ||
                    recorderState?.executionType === 'preview'
                  }
                />
              )
            }

            {/* Right Section: Contains Browser and Status Panel */}
            {/* Need a wrapper div for the pointer-events logic and size calculation */}
            <div
              ref={innerSplitPaneWrapperRef}
              className="h-full w-full"
              style={{ pointerEvents: 'auto' }}
            >
              {/* Render inner split pane only when container dimensions are known */}
              {innerWidth > 0 && innerHeight > 0 && (
                <TestSplitPane
                  split="vertical"
                  defaultSize={isAIChatPanelVisible ? innerWidth - rightPanelWidth : innerWidth}
                  onChange={(size) => {
                    if (isAIChatPanelVisible) {
                      setRightPanelWidth(innerWidth - size);
                    }
                  }}
                  onDragFinished={(size) => {
                    if (isAIChatPanelVisible) {
                      setRightPanelWidth(innerWidth - size);
                    }
                  }}
                  minSize={400}
                  maxSize={isAIChatPanelVisible ? innerWidth - MIN_AI_CHAT_PANEL_WIDTH : innerWidth}
                  resizerClassName={isAIChatPanelVisible ? resizerClassName : 'hidden'}
                  pane1Style={{ pointerEvents: 'auto' }}
                  pane2Style={{ pointerEvents: 'auto' }}
                  size={isAIChatPanelVisible ? innerWidth - rightPanelWidth : innerWidth}
                >
                  {/* Browser Panel */}
                  <div className="flex h-full w-full flex-col">
                    <TestSplitPane
                      split="horizontal"
                      size={isConsoleCollapsed ? innerHeight - 45 : innerHeight - consoleHeight} // 45 is the height of the tab
                      onChange={(size) => !isConsoleCollapsed && setConsoleHeight(innerHeight - size)}
                      onDragFinished={(size) => !isConsoleCollapsed && setConsoleHeight(innerHeight - size)}
                      maxSize={innerHeight - 100}
                      resizerClassName="h-[2px] bg-gray-300 dark:bg-gray-700 z-10 transition-colors duration-200 ease-in-out hover:bg-gray-400 dark:hover:bg-gray-600 cursor-row-resize"
                      resizerStyle={{
                        pointerEvents: isConsoleCollapsed ? 'none' : 'auto', // disable resize when console is collapsed
                      }}
                      pane1ClassName="overflow-hidden"
                      pane2ClassName="overflow-hidden"
                    >
                      {isLoading || !testContent ?
                        <div className="h-full w-full p-5">
                          <Skeleton className="h-full w-full rounded-lg" />
                        </div>
                      : <BrowserPanel
                          className="flex-1"
                          testContentId={testContent.id}
                          testType={testContent.test.testType}
                        />
                      }

                      {isLoading || !testContent ?
                        <div className="h-full w-full p-5">
                          <Skeleton className="h-full w-full rounded-lg" />
                        </div>
                      : <ConsolePanel
                          testContentId={testContent.id}
                          isCollapsed={isConsoleCollapsed}
                          onToggleCollapse={() => setIsConsoleCollapsed(!isConsoleCollapsed)}
                        />
                      }
                    </TestSplitPane>
                  </div>

                  {/* Status Panel */}
                  {!!isAIChatPanelVisible && (
                    <>
                      {isLoading || !testContent ?
                        <AIChatPanelSkeleton />
                      : <div className="h-full w-full">
                          {!!testContent?.id && (
                            <AIChatPanel
                              testContentId={testContent.id}
                              onClose={toggleAIChatPanel}
                            />
                          )}
                        </div>
                      }
                    </>
                  )}
                </TestSplitPane>
              )}

              {/* AI Chat Panel Toggle Button - shown when panel is hidden */}
              {!isAIChatPanelVisible && (
                <div className="absolute right-0 top-1/2 z-20 -translate-y-1/2 pr-2">
                  <Button
                    variant="subtle"
                    size="compact-md"
                    onClick={toggleAIChatPanel}
                    className={clsx(
                      'relative flex h-20 w-8 flex-col items-center justify-center rounded-l-md rounded-r-none border-y border-l border-gray-200 bg-white shadow-lg transition-all duration-200 hover:w-10 dark:bg-gray-800',
                      hasNewMessages && !isAIChatPanelVisible ?
                        'text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950'
                      : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700',
                    )}
                    title="Show AI Chat"
                  >
                    <IconChevronLeft
                      size={12}
                      className="mb-0.5"
                    />
                    <IconBell size={12} />
                    {!!hasNewMessages && !isAIChatPanelVisible && (
                      <Badge
                        size="xs"
                        variant="filled"
                        color="green"
                        className="absolute right-2 top-2 z-10 min-w-[14px] rounded-full px-0.5 py-[1px] text-[9px] leading-none"
                      >
                        {activeAiChatLog.length - lastSeenMessageCount}
                      </Badge>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </TestSplitPane>
        </div>
      </div>
    </>
  );
}
